import React, { useMemo } from 'react';

/**
 * غلاف مولَّد محلياً لكل إذاعة.
 *
 * يحل ثلاث مشاكل في آن:
 *  • كانت 95 صورة موصولة من Unsplash — تتعطل دون إنترنت، وتعتمد على طرف
 *    ثالث، وبلا إذن استخدام موثّق.
 *  • بعضها غير مناسب للمحتوى: أبو الهول والأهرامات كغلاف لإذاعة قرآن.
 *  • لا شيء منها شعار الإذاعة الحقيقي، فالقيمة التعريفية كانت صفراً.
 *
 * البديل: زخرفة هندسية إسلامية تُرسم SVG من معرّف الإذاعة — ثابتة لكل
 * إذاعة، مختلفة بينها، وتستعير لون الثيم فتتناغم مع أي مظهر.
 */

// إزاحات صبغة ضيّقة: المدى الواسع كان يدفع اللازورد إلى البنفسجي
// والوردي — ألوان تخرج عن طابع التطبيق.
const HUE_SHIFTS = [0, 12, -12, 20, -20, 8, -8, 16];

/** تجزئة ثابتة: نفس المعرّف يعطي دائماً نفس الزخرفة. */
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/** نجمة متعددة الرؤوس حول المركز (100,100). */
function starPoints(outer, inner, points, rotation) {
  const out = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = i * step + rotation;
    out.push(`${(100 + r * Math.cos(a)).toFixed(1)},${(100 + r * Math.sin(a)).toFixed(1)}`);
  }
  return out.join(' ');
}

export const StationArtwork = ({ station, className = '', rounded = false }) => {
  const art = useMemo(() => {
    const seed = hash(station?.id ?? station?.name ?? 'station');
    // خلط البتّات: `seed % 8` وحده يجعل معرّفات متقاربة تقع على نفس
    // الإزاحة، فتتشابه ألوان الإذاعات المتجاورة في القائمة.
    const mixed = (seed ^ (seed >>> 13)) >>> 0;
    return {
      id: `a${seed.toString(36)}`,
      hue: HUE_SHIFTS[mixed % HUE_SHIFTS.length],
      rotation: ((mixed >>> 3) % 16) * (Math.PI / 32),
      points: 8 + ((mixed >>> 7) % 2) * 4,
      lattice: 3 + ((mixed >>> 11) % 3),
      radius: rounded ? 10 : 0,
    };
  }, [station?.id, station?.name, rounded]);

  const flag = station?.countryFlag ?? '';
  const cell = 32 / art.lattice;

  return (
    // viewBox مربّع: كان 320×200، ومع preserveAspectRatio "slice" داخل
    // حاوية أضيق كان الجانبان يُقصّان فيختفي علم الدولة.
    <svg
      viewBox="0 0 200 200"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      role="img"
      aria-label={station?.name ? `غلاف ${station.name}` : 'غلاف إذاعة'}
    >
      <defs>
        <linearGradient id={`${art.id}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgb(var(--c-primary))" stopOpacity="0.14" />
          <stop offset="100%" stopColor="rgb(var(--c-secondary))" stopOpacity="0.09" />
        </linearGradient>
        <radialGradient id={`${art.id}-glow`} cx="50%" cy="48%" r="56%">
          <stop offset="0%" stopColor="rgb(var(--c-primary))" stopOpacity="0.18" />
          <stop offset="100%" stopColor="rgb(var(--c-primary))" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${art.id}-lattice`} width={cell} height={cell} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={cell} height={cell} fill="none" stroke="rgb(var(--c-primary))" strokeOpacity="0.13" strokeWidth="0.7" />
        </pattern>
      </defs>

      {/* الفلتر على الزخرفة وحدها؛ تطبيقه على الـ SVG كله يقلب ألوان العلم */}
      <g style={{ filter: `hue-rotate(${art.hue}deg)` }}>
        <rect width="200" height="200" fill="rgb(var(--c-surfaceAlt))" rx={art.radius} />
        <rect width="200" height="200" fill={`url(#${art.id}-bg)`} rx={art.radius} />
        <rect width="200" height="200" fill={`url(#${art.id}-lattice)`} rx={art.radius} />
        <rect width="200" height="200" fill={`url(#${art.id}-glow)`} rx={art.radius} />

        {/* نجمتان متراكبتان بإزاحة زاوية — أساس التشابك في الزخرفة الإسلامية.
            نسبة القطر الداخلي ≈ 0.62 تقرب من نجمة الزليج؛ الأصغر يعطي
            أشعة حادة تشبه الشمس لا النجمة. */}
        <polygon points={starPoints(58, 36, art.points, art.rotation)} fill="rgb(var(--c-primary))" fillOpacity="0.08" stroke="rgb(var(--c-primary))" strokeOpacity="0.34" strokeWidth="1.1" strokeLinejoin="round" />
        <polygon points={starPoints(58, 36, art.points, art.rotation + Math.PI / art.points)} fill="none" stroke="rgb(var(--c-secondary))" strokeOpacity="0.3" strokeWidth="0.9" strokeLinejoin="round" />
        <polygon points={starPoints(33, 26, art.points, art.rotation)} fill="rgb(var(--c-primary))" fillOpacity="0.14" stroke="rgb(var(--c-primary))" strokeOpacity="0.45" strokeWidth="0.9" strokeLinejoin="round" />
        <circle cx="100" cy="100" r="12" fill="rgb(var(--c-surface))" stroke="rgb(var(--c-primary))" strokeOpacity="0.4" strokeWidth="0.9" />
      </g>

      {/* علم الدولة — خارج الفلتر لتبقى ألوانه صحيحة، وفي موضع لا يُقصّ */}
      {flag && (
        <text x="100" y="168" fontSize="20" textAnchor="middle" opacity="0.9">
          {flag}
        </text>
      )}
    </svg>
  );
};
